import { patchState, signalStore, withComputed, withMethods, withProps, withState } from "@ngrx/signals";
import { AcademicGrid } from "./s25.academic.grid";
import { computed, linkedSignal, signal } from "@angular/core";
import { GridListItem, GridsService } from "../../services/grids.service";
import { s25Resource } from "../../util/s25.resource";
import { LynxService } from "../../services/lynx.service";
import { S25Util } from "../../util/s25-util";
import { S25Const } from "../../util/s25-const";
import { CustomAttributes } from "../../pojo/CustomAttributes";
import { InferSignal, InferStore } from "../../pojo/Util";
import { BOARD_CONST } from "../s25-swarm-schedule/s25.board.const";
import { S25Assert } from "../../util/s25.assert";

type State = {
    grid: GridListItem | undefined;
    broughtToFront: AcademicGrid.Item | undefined;
    // Bulk saving is not fully implemented
    saveType: AcademicGrid.SaveType;
};

export const AcademicGridStore = signalStore(
    // Managed state
    withState(<State>{
        grid: undefined,
        broughtToFront: undefined,
        saveType: "individual",
    }),
    // Resources
    withProps((store) => ({
        _expSecEnabledResource: s25Resource({ defaultValue: false, loader: LynxService.getExpSecEnabled }),
        _prefsResource: s25Resource({ loader: GridsService.getPreferences }),
        _gridDataResource: s25Resource({
            params: () => store.grid()?.id,
            loader: async ({ params: gridId }) => {
                if (!Number.isFinite(gridId)) return undefined;
                const gridResult = await GridsService.getData(gridId);
                if (gridResult.data) {
                    for (const event of gridResult.data.events) event.name = event.name?.trim() || "";
                    gridResult.data.events.sort((a, b) => a.name.localeCompare(b.name));
                }
                return gridResult.data;
            },
        }),
    })),
    // Init
    withProps((store) => {
        const status = s25Resource.combine(store._gridDataResource, store._prefsResource);
        const canEditSchedule = computed(() => !!(store._expSecEnabledResource.value() && store.grid()?.editSchedule));
        const canEditLocation = computed(() => !!(store.grid()?.editLocation && store.grid()?.locationQuery));

        const warningsConfig = linkedSignal(() => {
            const isBulk = store.saveType() === "bulk";
            const action = (
                onlyBulk: boolean,
                requireEditSchedule: boolean,
                requireEditLocation: boolean,
            ): AcademicGrid.WarningLevel => {
                if (onlyBulk && !isBulk) return "prevent";
                if (requireEditSchedule && !canEditSchedule()) return "info";
                if (requireEditLocation && !canEditLocation()) return "info";
                return "warn";
            };
            const config: AcademicGrid.WarningsConfig = {
                standardSchedules: { action: action(false, true, false) },
                instructorConflicts: { action: action(false, true, false) },
                locationConflicts: { action: action(true, true, true) },
                resourceConflicts: { action: action(true, true, false) },
                locationUndersized: { ratio: "RHC:CAP", threshold: 100, action: action(false, false, true) },
                locationOversized: { ratio: "RHC:CAP", threshold: 2, action: action(false, false, true) },
                missingFeaturePrefs: { action: action(false, false, true) },
                missingPartitionPrefs: { action: action(false, false, true) },
            };
            for (const conf of Object.values(config)) conf.maxSeverity = conf.action === "info" ? "info" : "prevent";
            return config;
        });

        const mode = signal<AcademicGrid.Mode>("schedule");

        const gridData = computed(() => store._gridDataResource.value());
        const timeSets = computed(() => (gridData() ? AcademicGrid.getTimeSets(gridData()!, gridData()!.events) : []));
        const locations = computed(() => gridData()?.spaces ?? {});
        const allInstructors = computed(() =>
            gridData() ? Object.values(AcademicGrid.getInstructorById(gridData()!)) : [],
        );

        const scheduleRows = computed(() => AcademicGrid.getScheduleRows(timeSets()));
        const instructorRows = computed(() => AcademicGrid.getInstructorRows(allInstructors()));
        const locationRows = computed(() => AcademicGrid.getLocationRows(allLocations()));
        const rows = computed(() => {
            const localMode = mode();
            switch (localMode) {
                case "schedule":
                    return scheduleRows();
                case "instructor":
                    return instructorRows();
                case "location":
                    return sortedLocationRows();
                default:
                    S25Assert.never(localMode, "Uncovered mode");
            }
        });
        const rowIndex = computed(() => new Map(rows().map((row, i) => [row.id, i])));

        const grabbedEntry = linkedSignal<AcademicGrid.GrabbedEntry | undefined>(() => {
            rows(); // Reset on changes to rows
            return undefined;
        });

        const locationConflicts = computed(() => {
            const data = gridData();
            if (!data) return [];
            return AcademicGrid.getLocationConflicts({
                data,
                warningsConfig,
                locations,
                mode,
                rowIndex,
                allRows: rows,
                grabbedEntry,
            });
        });
        const eventItems = computed(() => {
            const data = gridData();
            if (!data) return [];
            return AcademicGrid.getItems({
                data,
                timeSets: timeSets(),
                rowIndex,
                warningsConfig,
                mode,
                locations,
                allRows: rows,
                grabbedEntry,
                instructorTimeIndex,
            });
        });

        const items = computed(() => {
            return [...locationConflicts(), ...eventItems()];
        });
        const instructorTimeIndex = computed(
            (): AcademicGrid.InstructorTimeIndex => AcademicGrid.buildInstructorTimeIndex(eventItems()),
        );

        const itemDows = computed(() => {
            const itemDows = S25Util.array.unique(timeSets()?.map((timeSet) => timeSet.dow) ?? []);
            const sortIndex = new Map(BOARD_CONST.allDows.map((dow, i) => [dow, i]));
            itemDows.sort((a, b) => {
                const [A, B] = [sortIndex.get(a) ?? Infinity, sortIndex.get(b) ?? Infinity];
                return A < B ? -1 : A === B ? 0 : 1;
            });
            return itemDows;
        });

        const allDows = computed(() => S25Util.array.unique([...BOARD_CONST.allDows, ...itemDows()]));

        const featurePrefs = computed(() => {
            const prefs = gridData()?.events.flatMap((e) => e.profiles.flatMap((p) => p.featurePreferences)) ?? [];
            return S25Util.array.unique(prefs, (pref) => pref.id);
        });
        const partitionPrefs = computed(() => {
            const events = gridData()?.events ?? [];
            const sectionPrefs = events.flatMap((e) => e.profiles.flatMap((p) => p.partitionPreferences));
            const orgPrefs = events.flatMap((e) => e.primaryOrganization?.partitionPreferences ?? []).flat();
            return S25Util.array.unique([...sectionPrefs, ...orgPrefs], (pref) => pref.id);
        });
        const instructors = computed(() => {
            const instructors = gridData()
                ?.events.map((e) => e.roles.find((r) => r.role === S25Const.instructorRole.event))
                .filter(S25Util.isDefined);
            return S25Util.array
                .unique(instructors ?? [], (instructor) => instructor.id)
                .map((instructor) => ({ id: instructor.id, name: AcademicGrid.getContactName(instructor) }));
        });
        const sectionTypes = computed(() => {
            const types = gridData()
                ?.events.map((e) => e.attributes.find((a) => a.id === CustomAttributes.system.sectionType.id))
                .filter(S25Util.isDefined);
            return S25Util.array
                .unique(types ?? [], (type) => type.id)
                .map((type) => ({ id: type.id, name: type.value }));
        });
        const subjectCodes = computed(() => {
            const subjects = gridData()
                ?.events.filter((e) => AcademicGrid.isLynx(e) && e.primaryOrganization)
                .map((e) => e.primaryOrganization!.name);
            return S25Util.array.unique(subjects ?? []);
        });
        const subTerms = computed(() => {
            const terms = gridData()
                ?.events.map((e) => e.attributes.find((a) => a.id === CustomAttributes.system.subTerm.id))
                .filter(S25Util.isDefined);
            return S25Util.array
                .unique(terms ?? [], (term) => term.id)
                .map((term) => ({ id: term.id, name: term.value }));
        });
        const features = computed(() => {
            const features = Object.values(gridData()?.spaces ?? {}).flatMap((location) => location.features);
            return S25Util.array.unique(features, (feature) => feature.id);
        });
        const partitions = computed(() => {
            const partitions = Object.values(gridData()?.spaces ?? {})
                .map((location) => location.partition)
                .filter(S25Util.isDefined);
            return S25Util.array.unique(partitions, (partition) => partition.id);
        });

        const hasLocationFilters = computed(() => {
            if (mode() !== "location") return false;
            if (!store.grid()?.locationQuery) return false;
            return true;
        });
        const filters = computed(() =>
            AcademicGrid.getFilters({
                hasLocationFilters,
                dowOptions: allDows(),
                featurePrefs: featurePrefs(),
                partitionPrefs: partitionPrefs(),
                partitions: partitions(),
                sectionTypes: sectionTypes(),
                instructors: instructors(),
                subTerms: subTerms(),
                subjectCodes: subjectCodes(),
                features: features(),
                locations: locations(),
            }),
        );
        const hasActiveItemFilter = computed(() =>
            filters().items.filters.some((filter) => {
                if (!filter.active()) return false;
                return filter.showText ? filter.showText() : true;
            }),
        );
        const prioritizeLocations = linkedSignal({
            source: () => [mode(), hasActiveItemFilter()], // Reset when these change
            computation: () => <Set<number | "none"> | undefined>undefined,
        });
        const sortedLocationRows = computed(
            () => {
                return AcademicGrid.sortLocations({
                    rows: locationRows(),
                    sortItem: sortLocationsByItem(),
                    prioritizedLocations: prioritizeLocations(),
                    warningsConfig: warningsConfig(),
                    locations: locations(),
                });
            },
            { equal: S25Util.array.isEqual },
        );

        const sortLocationsByItem = linkedSignal({
            source: () => [mode(), store.grid()], // Reset when these change
            computation: () => <AcademicGrid.Item | undefined>undefined,
        });

        const eventLocations = computed(() => {
            const data = gridData();
            if (!data) return [];
            const locationIds = (data.events ?? []).flatMap((event) =>
                event.profiles.flatMap((profile) =>
                    profile.reservations.flatMap((reservation) => reservation.locations.map((loc) => loc.id)),
                ),
            );
            return S25Util.array.unique(locationIds, (id) => id).map((id) => data.spaces[id]);
        });
        const allLocations = computed(() => {
            const spaces = gridData()?.spaces;
            if (!spaces) return [];
            return Object.values(spaces);
        });

        const startHour = computed(() => store._prefsResource.value()?.startHour ?? 0);
        const endHour = computed(() => store._prefsResource.value()?.endHour ?? 17);
        const hasShadows = computed(() => mode() === "instructor" || mode() === "location");
        const defaultSchedule = computed(() => gridData()?.defaultSchedule);
        const locationRelationships = computed(() => AcademicGrid.getFullLocationRelationships(locations()));

        return {
            gridData,
            status,
            canEditSchedule,
            canEditLocation,
            mode,
            locations,
            warningsConfig,
            defaultSchedule,
            standardSchedule: linkedSignal(() =>
                store.grid()?.defaultSchedule ? { itemId: store.grid()!.defaultSchedule } : undefined,
            ),
            visibleDows: linkedSignal<string[]>(() => itemDows(), { equal: S25Util.array.allSame }),
            allDows,
            itemDows,
            items,
            instructorTimeIndex,
            scheduleRows,
            instructorRows,
            locationRows,
            rows,
            rowIndex,
            eventLocations,
            allLocations,
            locationConflicts,
            filters,
            sortLocationsByItem,
            message: computed(() => (gridData()?.events.length === 0 ? "No events in search" : undefined)),
            startHour,
            endHour,
            hasShadows,
            grabbedEntry,
            hasActiveItemFilter,
            prioritizeLocations,
            locationRelationships,
            frozenRows: computed(() => (["instructor", "location"].includes(mode()) ? 1 : 0)),
        };
    }),
    // Filters
    withProps((store) => {
        const filterTabs = computed(() =>
            Object.entries(store.filters())
                .map((entry) => ({ id: entry[0], ...entry[1] }))
                .filter((category) => {
                    if ("hidden" in category && category.hidden()) return false;
                    return true;
                }),
        );

        const filterTab = linkedSignal<InferSignal<typeof filterTabs>, InferSignal<typeof filterTabs>[0]>({
            source: filterTabs,
            computation: (tabs, previous) => {
                const prev = tabs.find((tab) => tab.id === previous?.value.id);
                return prev ? prev : tabs[0];
            },
        });

        const dowFilter = computed((): AcademicGrid.Filter<"item"> => {
            return store.filters().items.filters.find((filter) => filter.id === "dows")!;
        });

        return {
            filterTabs,
            filterTab,
            dowFilter,
        };
    }),
    withComputed((store) => ({
        canEditX: () => store.canEditSchedule(),
        canEditY: () => store.canEditLocation() && store.mode() === "location",
    })),
    // State methods
    withMethods((store) => ({
        setGrid: (grid: GridListItem | undefined) => {
            patchState(store, { grid: grid });
        },
        sortLocationsByRelevance: (item: AcademicGrid.Item | undefined) => {
            store.sortLocationsByItem.set(item);
        },
        bringToFront: (item: AcademicGrid.Item) => {
            patchState(store, { broughtToFront: item });
        },
        getRowById: (rowId: string) => {
            const index = store.rowIndex();
            if (!index.has(rowId)) return undefined;
            return store.rows()[index.get(rowId)!];
        },
        getOverlappingInstructors: (item: AcademicGrid.Item) => {
            return AcademicGrid.getOverlappingInstructors(store.instructorTimeIndex(), item);
        },
        getInstructorConflicts: (
            item: AcademicGrid.Item,
            overlaps: readonly AcademicGrid.Item[],
            is24Hours = false,
        ) => {
            return AcademicGrid.getInstructorConflicts(item, overlaps, is24Hours);
        },
    })),
    // Other methods
    withMethods((store) => ({
        clearFilters: (clearType?: keyof AcademicGridStoreInferred["filters"]) => {
            for (const [type, { filters }] of Object.entries(store.filters())) {
                if (!!clearType && type !== clearType) continue;
                for (const filter of filters) filter.clear();
            }
        },
    })),
);

export type AcademicGridStore = InstanceType<typeof AcademicGridStore>;
export type AcademicGridStoreInferred = InferStore<typeof AcademicGridStore>;
